import { useEffect, useState } from "react";
import {
  Activity,
  BadgeCheck,
  CreditCard,
  Download,
  FileText,
  Flame,
  Mail,
  MessageSquare,
  Sparkles,
  Users,
} from "lucide-react";
import api, { dateLong } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

const TABS = ["overview", "users", "reservations", "feedback", "payments", "usage"];

const Stat = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="card-flat p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="font-mono-num mt-3 text-3xl font-semibold">{value}</p>
    {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
  </div>
);

const Cell = ({ children, mono }) => (
  <td className={`px-3 py-2.5 align-middle ${mono ? "font-mono-num" : ""}`}>{children}</td>
);

const PLAN_STYLE = {
  pro: "bg-emerald-50 text-emerald-700",
  pass: "bg-indigo-50 text-indigo-700",
  beta: "bg-amber-50 text-amber-700",
  free: "bg-slate-100 text-slate-600",
};

const Admin = () => {
  const { t } = useI18n();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [error, setError] = useState(false);

  useEffect(() => {
    const paths = {
      overview: "/admin/overview",
      users: "/admin/users",
      reservations: "/admin/reservations",
      feedback: "/admin/feedback",
      payments: "/admin/transactions",
      usage: "/admin/usage",
    };
    api
      .get(paths[tab])
      .then(({ data: res }) => setData((prev) => ({ ...prev, [tab]: res })))
      .catch(() => setError(true));
  }, [tab]);

  const current = data[tab];

  if (error)
    return (
      <p className="text-sm text-red-600" data-testid="admin-error">
        {t("launch.error")}
      </p>
    );

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight lg:text-3xl">{t("admin.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("admin.subtitle")}</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {TABS.map((key) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              tab === key
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            {t(`admin.tab.${key}`)}
          </button>
        ))}
      </div>

      {!current && <p className="text-sm text-slate-500">{t("common.loading")}</p>}

      {tab === "overview" && current && (
        <div className="space-y-6" data-testid="admin-overview">
          <div className="card-flat p-6" data-testid="admin-funnel">
            <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Activity className="h-4 w-4" /> {t("admin.funnel")}
            </h2>
            <div className="mt-5 grid gap-6 sm:grid-cols-4">
              {[
                { label: t("admin.visitors"), value: current.funnel?.uniques_30d ?? 0,
                  hint: `${current.funnel?.views_30d ?? 0} ${t("admin.views")}`, id: "visitors" },
                { label: t("admin.signups"), value: current.users_30d,
                  hint: `${current.funnel?.signup_rate ?? 0}% ${t("admin.ofVisitors")}`, id: "signups" },
                { label: t("admin.reserved"), value: current.seats_reserved,
                  hint: `${current.funnel?.reserve_rate ?? 0}% ${t("admin.ofVisitors")}`, id: "reserved" },
                { label: t("admin.today"), value: current.funnel?.uniques_today ?? 0,
                  hint: `${current.funnel?.views_today ?? 0} ${t("admin.views")}`, id: "today" },
              ].map((s) => (
                <div key={s.id} data-testid={`admin-funnel-${s.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {s.label}
                  </p>
                  <p className="font-mono-num mt-2 text-3xl font-semibold">{s.value}</p>
                  <p className="mt-1 text-xs text-slate-500">{s.hint}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card-flat p-6" data-testid="admin-funnel-chain">
            <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Activity className="h-4 w-4" /> {t("admin.chainTitle")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t("admin.chainHint")}</p>
            <div className="mt-5 space-y-2">
              {(current.funnel?.steps || []).map((step, i) => {
                const worst = current.funnel?.worst_step === step.key;
                return (
                  <div key={step.key} data-testid={`chain-${step.key}`}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="font-medium">{t(`admin.step.${step.key}`)}</span>
                      <span className="font-mono-num shrink-0 text-slate-500">
                        <b className="text-slate-900">{step.count}</b> · {step.of_top}%
                        {i > 0 && ` · ${step.of_prev}% ${t("admin.ofPrev")}`}
                      </span>
                    </div>
                    <div className="mt-1.5 h-7 w-full overflow-hidden rounded-md bg-slate-100">
                      <div
                        style={{ width: `${Math.max(step.of_top, 1.5)}%` }}
                        className={`h-full rounded-md transition-all duration-500 ${
                          worst ? "bg-red-500" : "bg-blue-600"
                        }`}
                      />
                    </div>
                    {i > 0 && step.lost > 0 && (
                      <p
                        className={`mt-1 text-xs ${
                          worst ? "font-semibold text-red-600" : "text-slate-400"
                        }`}
                      >
                        −{step.lost} {t("admin.lostHere")}
                        {worst && ` · ${t("admin.biggestDrop")}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-6 grid gap-4 border-t border-slate-200 pt-5 sm:grid-cols-3">
              <div data-testid="admin-activation">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t("admin.activation")}
                </p>
                <p className="font-mono-num mt-2 text-3xl font-semibold">
                  {current.funnel?.activation_rate ?? 0}%
                </p>
                <p className="mt-1 text-xs text-slate-500">{t("admin.activationHint")}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t("admin.signupRate")}
                </p>
                <p className="font-mono-num mt-2 text-3xl font-semibold">
                  {current.funnel?.signup_rate ?? 0}%
                </p>
                <p className="mt-1 text-xs text-slate-500">{t("admin.ofVisitors")}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t("admin.reserveRate")}
                </p>
                <p className="font-mono-num mt-2 text-3xl font-semibold">
                  {current.funnel?.reserve_rate ?? 0}%
                </p>
                <p className="mt-1 text-xs text-slate-500">{t("admin.ofVisitors")}</p>
              </div>
            </div>
          </div>

          <div className="card-flat p-6" data-testid="admin-sources">
            <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <Activity className="h-4 w-4" /> {t("admin.sources")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t("admin.sourcesHint")}</p>
            {(current.funnel?.sources || []).length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">{t("admin.noSources")}</p>
            ) : (
              <div className="mt-5 space-y-3">
                {current.funnel.sources.map((s) => (
                  <div key={s.source} data-testid={`source-${s.source}`}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium capitalize">{s.source}</span>
                      <span className="font-mono-num text-slate-500">
                        <b className="text-slate-900">{s.count}</b> · {s.rate}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        style={{ width: `${Math.max(s.rate, 2)}%` }}
                        className="h-full rounded-full bg-emerald-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              icon={Users}
              label={t("admin.users")}
              value={current.users_total}
              hint={`${current.users_30d} ${t("admin.last30")} · ${current.users_active_7d} ${t("admin.active7")}`}
              testid="admin-stat-users"
            />
            <Stat
              icon={Flame}
              label={t("admin.reserved")}
              value={`${current.seats_reserved} / ${current.seats_total}`}
              hint={`${current.waitlist_total} ${t("admin.waitlist")} · ${current.waitlist_converted} ${t("launch.converted")}`}
              testid="admin-stat-seats"
            />
            <Stat
              icon={CreditCard}
              label={t("admin.revenue")}
              value={usd(current.revenue_total_usd)}
              hint={`MRR ${usd(current.mrr_usd)} · ${current.pass_sold} pass`}
              testid="admin-stat-revenue"
            />
            <Stat
              icon={Sparkles}
              label={t("admin.aiMonth")}
              value={current.ai_actions_month}
              hint={current.ai_by_kind.map((k) => `${k.kind} ${k.count}`).join(" · ") || "—"}
              testid="admin-stat-ai"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              icon={FileText}
              label={t("admin.documents")}
              value={current.documents_total}
              hint={`${current.documents_30d} ${t("admin.last30")}`}
              testid="admin-stat-docs"
            />
            <Stat
              icon={FileText}
              label={t("admin.quotes")}
              value={current.documents_by_type?.quote || 0}
              hint={`${current.documents_by_type?.invoice || 0} ${t("admin.invoices")}`}
              testid="admin-stat-types"
            />
            <Stat
              icon={Users}
              label={t("admin.clients")}
              value={current.clients_total}
              testid="admin-stat-clients"
            />
            <Stat
              icon={BadgeCheck}
              label={t("admin.mode")}
              value={current.sales_mode === "prebook" ? t("admin.modePrebook") : t("admin.modeLive")}
              hint={
                current.emails_configured
                  ? `${t("admin.emailOn")} · ${current.notify_email}`
                  : t("admin.emailOff")
              }
              testid="admin-stat-mode"
            />
          </div>
        </div>
      )}

      {tab === "users" && current && (
        <div className="card-flat overflow-x-auto" data-testid="admin-users-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3">{t("admin.col.email")}</th>
                <th className="px-3 py-3">{t("admin.col.plan")}</th>
                <th className="px-3 py-3">{t("admin.col.docs")}</th>
                <th className="px-3 py-3">{t("admin.col.ai")}</th>
                <th className="px-3 py-3">{t("admin.col.seat")}</th>
                <th className="px-3 py-3">{t("admin.col.created")}</th>
                <th className="px-3 py-3">{t("admin.col.lastLogin")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {current.users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-${u.email}`} className="hover:bg-slate-50">
                  <Cell>
                    <span className="font-semibold">{u.email}</span>
                    <span className="block text-xs text-slate-500">
                      {u.name}
                      {u.company ? ` · ${u.company}` : ""}
                    </span>
                  </Cell>
                  <Cell>
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${
                        PLAN_STYLE[u.plan] || PLAN_STYLE.free
                      }`}
                    >
                      {u.plan}
                    </span>
                  </Cell>
                  <Cell mono>{u.documents}</Cell>
                  <Cell mono>
                    {u.ai_used_month}
                    {u.ai_limit ? ` / ${u.ai_limit}` : ""}
                  </Cell>
                  <Cell mono>{u.seat_no ? `#${u.seat_no}` : "—"}</Cell>
                  <Cell mono>{dateLong(u.created_at)}</Cell>
                  <Cell mono>{u.last_login ? dateLong(u.last_login) : "—"}</Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "feedback" && current && (
        <div className="space-y-4" data-testid="admin-feedback">
          <div className="card-flat p-6">
            <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
              <MessageSquare className="h-4 w-4" /> {t("admin.feedbackTitle")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {current.total} {t("admin.rows")} · {current.surveys_sent}{" "}
              {t("admin.feedbackSent")}
            </p>
            <div className="mt-5 space-y-3">
              {current.counts.map((row) => (
                <div key={row.reason} data-testid={`admin-feedback-${row.reason}`}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="font-medium text-slate-800">{row.label}</span>
                    <span className="font-mono-num font-semibold">{row.count}</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-slate-900"
                      style={{
                        width: `${Math.round((row.count / Math.max(current.total, 1)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
              {current.total === 0 && (
                <p className="text-sm text-slate-500" data-testid="admin-no-feedback">
                  {t("admin.feedbackEmpty")}
                </p>
              )}
            </div>
          </div>
          {current.requests?.length > 0 && (
            <div className="card-flat p-6" data-testid="admin-requests">
              <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
                <MessageSquare className="h-4 w-4" /> {t("admin.requestsTitle")}
              </h2>
              <ul className="mt-5 space-y-5">
                {current.requests.map((r) => (
                  <li key={`${r.created_at}-${r.email}`} className="border-l-2 border-slate-900 pl-4">
                    <p className="text-sm italic text-slate-800">“{r.text}”</p>
                    <p className="mt-1.5 text-xs text-slate-500">
                      {[r.job, r.email, dateLong(r.created_at)].filter(Boolean).join(" · ")}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {current.total > 0 && (
            <div className="card-flat overflow-x-auto" data-testid="admin-feedback-table">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-3">{t("admin.col.email")}</th>
                    <th className="px-3 py-3">{t("admin.feedbackTitle")}</th>
                    <th className="px-3 py-3">{t("admin.col.received")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {current.answers.map((a) => (
                    <tr key={`${a.email}-${a.reason}`} className="hover:bg-slate-50">
                      <Cell>{a.email}</Cell>
                      <Cell>{a.label}</Cell>
                      <Cell mono>{dateLong(a.created_at)}</Cell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "reservations" && current && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {current.total} {t("admin.rows")}
            </p>
            <a href={`${api.defaults.baseURL}/admin/reservations.csv`} data-testid="admin-export-csv">
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" /> {t("admin.exportCsv")}
              </Button>
            </a>
          </div>
          <div className="card-flat overflow-x-auto" data-testid="admin-reservations-table">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">{t("admin.col.seat")}</th>
                  <th className="px-3 py-3">{t("admin.col.email")}</th>
                  <th className="px-3 py-3">{t("admin.col.source")}</th>
                  <th className="px-3 py-3">{t("admin.col.reserved")}</th>
                  <th className="px-3 py-3">{t("admin.col.converted")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {current.reservations.map((r) => (
                  <tr key={r.email} className="hover:bg-slate-50">
                    <Cell mono>{r.seat_no ? `#${r.seat_no}` : "—"}</Cell>
                    <Cell>{r.email}</Cell>
                    <Cell>{r.source || "—"}</Cell>
                    <Cell mono>{r.reserved ? dateLong(r.reserved_at) : "—"}</Cell>
                    <Cell mono>
                      {r.converted_at ? (
                        <span className="font-semibold text-emerald-700">
                          {dateLong(r.converted_at)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </Cell>
                  </tr>
                ))}
              </tbody>
            </table>
            {current.total === 0 && (
              <p className="px-3 py-6 text-sm text-slate-500" data-testid="admin-no-reservations">
                {t("admin.empty")}
              </p>
            )}
          </div>
        </div>
      )}

      {tab === "payments" && current && (
        <div className="card-flat overflow-x-auto" data-testid="admin-payments-table">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3">{t("admin.col.email")}</th>
                <th className="px-3 py-3">{t("admin.col.product")}</th>
                <th className="px-3 py-3">{t("admin.col.amount")}</th>
                <th className="px-3 py-3">{t("admin.col.status")}</th>
                <th className="px-3 py-3">{t("admin.col.created")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {current.transactions.map((r) => (
                <tr key={r.session_id} className="hover:bg-slate-50">
                  <Cell>{r.email}</Cell>
                  <Cell>{r.lookup_key}</Cell>
                  <Cell mono>{usd(r.amount)}</Cell>
                  <Cell>
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${
                        r.payment_status === "paid"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {r.payment_status}
                    </span>
                  </Cell>
                  <Cell mono>{dateLong(r.created_at)}</Cell>
                </tr>
              ))}
            </tbody>
          </table>
          {current.total === 0 && (
            <p className="px-3 py-6 text-sm text-slate-500" data-testid="admin-no-payments">
              {t("admin.empty")}
            </p>
          )}
        </div>
      )}

      {tab === "usage" && current && (
        <div className="card-flat p-6" data-testid="admin-usage-chart">
          <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
            <Activity className="h-4 w-4" /> {t("admin.usage30")}
          </h2>
          <div className="mt-6 flex h-40 items-end gap-1">
            {current.series.map((d) => {
              const max = Math.max(...current.series.map((s) => s.documents), 1);
              return (
                <div key={d.day} className="group relative flex-1" title={`${d.day}: ${d.documents}`}>
                  <div
                    className="w-full rounded-t bg-indigo-500/80 transition-all duration-300 group-hover:bg-indigo-600"
                    style={{ height: `${Math.max((d.documents / max) * 150, d.documents ? 4 : 1)}px` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-xs text-slate-500">
            <span>{current.series[0]?.day}</span>
            <span>{current.series[current.series.length - 1]?.day}</span>
          </div>
          <p className="mt-4 text-sm text-slate-600">
            <Mail className="mr-1.5 inline h-3.5 w-3.5" />
            {t("admin.notifyNote")}{" "}
            {data.overview?.notify_email && (
              <b data-testid="admin-notify-email">{data.overview.notify_email}</b>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default Admin;
